import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface LcQuote {
  amountOut: string;
  feeAmount: string;
  priceImpactBps: number;
  niceMode?: {
    adjustedInput: string;
    cleanOutput: string;
    roundingSavings: string;
  };
}

interface UseLcQuoteReturn {
  quote: LcQuote | null;
  loading: boolean;
  error: string | null;
}

export function useLiquidityCacheQuote(
  poolId: string | undefined,
  direction: string | undefined,
  amount: string | undefined,
  niceMode?: boolean,
): UseLcQuoteReturn {
  // Debounce the parameters: only update the "committed" values after 300ms of stability
  const [debouncedParams, setDebouncedParams] = useState({ poolId, direction, amount, niceMode });
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedParams({ poolId, direction, amount, niceMode });
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [poolId, direction, amount, niceMode]);

  const canFetch =
    !!debouncedParams.poolId &&
    !!debouncedParams.direction &&
    !!debouncedParams.amount &&
    debouncedParams.amount !== '0';

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.liquidityCaches.quote(debouncedParams.poolId ?? '', {
      direction: debouncedParams.direction,
      amount: debouncedParams.amount,
      niceMode: debouncedParams.niceMode,
    }),
    queryFn: () =>
      api.getLiquidityCacheQuote(
        debouncedParams.poolId!,
        debouncedParams.direction!,
        debouncedParams.amount!,
        debouncedParams.niceMode,
      ),
    enabled: canFetch,
  });

  return {
    quote: canFetch ? (data ?? null) : null,
    loading: isLoading && canFetch,
    error: error instanceof Error ? error.message : error ? 'Failed to get quote' : null,
  };
}
